import { useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import useDevices from './useDevices'
import useProfile from './useProfile'
import useProtectedApiReady from './helpers/useProtectedApiReady'
import TrackerManagementExperienceActions from '../actions/experiences/TrackerManagement'
import FilterExpression from '../../modules/utilities/list/FilterExpression'
import { filterRowsByViewerScope } from '../../modules/ac/visibilityScope'

/**
 * Returns all devices and trackers from the store in a non-normalized fashion.
 * This hook fetches both devices (via useDevices) and trackers (via TrackerManagementExperience).
 * Since trackers are stored in the same DeviceDataStore as devices, they are automatically merged.
 *
 * Scoped viewers (client/operator) are filtered by profile.deviceIds as defense-in-depth;
 * server list endpoints are the security boundary.
 *
 * @param {boolean} shouldFetchTrackers - Whether to fetch trackers (default: true)
 * @returns {Object} - Map of all devices and trackers by their id
 */
const useAllDevicesAndTrackers = (shouldFetchTrackers = true) => {
  const dispatch = useDispatch()
  const protectedApiReady = useProtectedApiReady()
  const profile = useProfile()

  // Fetch all devices (this will get all items from DeviceDataStore)
  const allDevices = useDevices(null, true, true)

  // Fetch all trackers on mount (they get stored in the same DeviceDataStore)
  // Use useLayoutEffect to ensure this runs synchronously before paint
  useLayoutEffect(
    () => {
      if (!protectedApiReady || !shouldFetchTrackers) {
        return
      }
      dispatch(
          TrackerManagementExperienceActions.list.fetch(
            10000, // High limit to fetch all trackers
            0,
            [], // sort parameters (empty - no sorting)
            [], // filter parameters (empty - fetch all trackers)
            FilterExpression.EXPRESSION_TYPE.AND // won't be used since filters is empty
          )
        )
    },
    [dispatch, protectedApiReady, shouldFetchTrackers]
  )

  return useMemo(() => {
    if (!allDevices || typeof allDevices !== 'object') {
      return allDevices
    }
    const rows = Object.values(allDevices)
    const scopedRows = filterRowsByViewerScope(profile, rows)
    if (scopedRows === rows) {
      return allDevices
    }
    return scopedRows.reduce((acc, device) => {
      if (device?.id != null) {
        acc[device.id] = device
      }
      return acc
    }, {})
  }, [allDevices, profile])
}

export default useAllDevicesAndTrackers
